import React, { useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { StoreState } from "../../../../store/createStore";
import Loading from "../../../../components/Loading";
import { findMethods, findMethodsAll, methodSetSelected } from "../../../../store/modules/parameter/actions";
import { useTranslation } from '../../../../hooks/useTranslation';
import { AddButton, ButtonContent, CardButton, CardName, CardNumber, Container, ContainerMethod, ContainerMethodCardData, ContainerMethodData, ContainerPayments, Title, TitleCashOnDelivery } from "./styles";
import { MdAdd, MdClose, MdEdit } from "react-icons/md";
import { ImCheckboxChecked, ImCheckboxUnchecked } from "react-icons/im";
import PaymentCreateType from "../../../../components/AccountPayment/PaymentCreateType";
import { usePaymentMethod } from "../../../../hooks/usePaymentMethod";
import { checkoutSetShippingPrice, setMethodPaymentSelected } from "../../../../store/modules/checkout/actions";
import { UserCard } from "../../../../store/modules/auth/types";
import { MethodsProps } from "../../../../store/modules/parameter/types";
import PixIcon from "../../../../components/IconPix";
import InfoImage from '../../../../assets/informacoes.svg';
import { useCheckoutInfo } from "../../../../hooks/useCheckoutInfo";
import LogoBrand from "../../../../components/ReactCreditCard/logoBrand";

const PaymentList: React.FC = () => {
    const {
        openViewCreatePayment,
        handledMethodAdd,
        handledMethodRemove,
        handledMethodEdit,
        handledSetMethod,
        methodSelected,
        onSetOpenViewCreatePayment,
        values
    } = usePaymentMethod();
    const {
        addressSelected,
    } = useCheckoutInfo();
    const dispatch = useDispatch();
    const { onTranslation: t } = useTranslation();
    const { methodsType, methods: cards, loading, address } = useSelector((state: StoreState) => state.parameter);
    const { methodPaymentSelected } = useSelector((state: StoreState) => state.checkout);

    useEffect(() => {
        if (methodsType.methodsLength === 0) {
            dispatch(findMethodsAll());
        }

        dispatch(findMethods());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onSetMethodPaymentSelectedCard = useCallback((card: UserCard) => {
        dispatch(checkoutSetShippingPrice(address?.shipping?.price || 0));
        dispatch(setMethodPaymentSelected({
            cards: [card],
            cashOnDelivery: [],
            others: []
        }))
        dispatch(methodSetSelected(card));
    }, [dispatch, address]);

    const onSetMethodPaymentSelectedCashOnDelivery = useCallback((method: MethodsProps) => {
        dispatch(checkoutSetShippingPrice(address?.shipping?.price_with_return || 0));
        dispatch(setMethodPaymentSelected({
            cards: [],
            cashOnDelivery: [method],
            others: []
        }));
    }, [dispatch, address]);

    const onSetMethodPaymentSelectedOther = useCallback((method: MethodsProps) => {
        dispatch(checkoutSetShippingPrice(address?.shipping?.price || 0));
        dispatch(setMethodPaymentSelected({
            cards: [],
            cashOnDelivery: [],
            others: [method]
        }));
    }, [dispatch, address]);

    useEffect(() => {
        if (cards && cards.length > 0 && 
            methodPaymentSelected.cards.length === 0 &&
            methodPaymentSelected.cashOnDelivery.length === 0 &&
            methodPaymentSelected.others.length === 0
        ) {
            cards.forEach(card => {
                if (card.selected === 1 && card.id) {
                    onSetMethodPaymentSelectedCard(card);
                    dispatch(methodSetSelected(card));
                }
            });
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cards, onSetMethodPaymentSelectedCard, dispatch]);

    if (openViewCreatePayment) {
        return (
            <PaymentCreateType
                handledSetMethod={handledSetMethod}
                methodSelected={methodSelected}
                onSetOpenViewCreatePayment={onSetOpenViewCreatePayment}
                values={values}
            />
        )
    }

    if (loading) {
        return (
            <Container bJustifyContentCenter>
                <Loading size={160} bShowText />
            </Container>
        )
    }

    return (
        <Container>
            {
                methodsType.options.cards.length > 0 && (
                    <ContainerPayments>
                        <Title>
                            <span>{t('main.component_account_payment.my_cards')}</span>
                            <AddButton title="Adicionar Cartão" onClick={handledMethodAdd}>
                                <MdAdd size={28} />
                                <span>Adicionar Cartão</span>
                            </AddButton>
                        </Title>
                        {
                            (!cards || cards.length === 0) && (
                                <div>{t('main.component_account_payment.without_card')}</div>
                            )
                        }
                        {
                            cards && cards.length > 0 && (
                                cards.map(card => (
                                    <ContainerMethod key={`card-${card.id}`}>
                                        <ContainerMethodCardData>
                                            <CardButton
                                                id={`card-${card.id}`}
                                                title={'Selecione a forma de pagamento'} onClick={() => {
                                                    onSetMethodPaymentSelectedCard(card)
                                                }}>
                                                {methodPaymentSelected.cards.length > 0 && methodPaymentSelected.cards[0].id === card.id ? <ImCheckboxChecked color="var(--primary)" size={28} /> : <ImCheckboxUnchecked color="#ccc" size={28} />}
                                            </CardButton>
                                            <div>
                                                <div><LogoBrand brand={card.card_brand || ''}/></div>
                                                <div>
                                                    <CardNumber><span>{'****.****.****.' + card.card_last_four}</span></CardNumber>
                                                    <CardName><span> {card.full_name} - {card.expiry_date} - {methodsType.options.cards?.find(methodall => methodall.media_id === card.media_id)?.name || ''}</span></CardName>
                                                </div>
                                            </div>
                                        </ContainerMethodCardData>
                                        <ButtonContent>
                                            <CardButton title={t('main.component_account_payment.change_card')} onClick={() => { handledMethodEdit(card) }}>
                                                <MdEdit color="var(--btn-finish)" size={28} />
                                            </CardButton>
                                            <CardButton title={t('main.component_account_payment.remove_card')} onClick={() => { handledMethodRemove(card.id ? card.id : 0) }}>
                                                <MdClose color="var(--btn-canc)" size={28} />
                                            </CardButton>
                                        </ButtonContent>
                                    </ContainerMethod>
                                ))
                            )
                        }
                    </ContainerPayments>
                )
            }
            {
                methodsType.options.others.length > 0 && (
                    <ContainerPayments>
                        <Title>
                            <span>Outras opções</span>
                        </Title>
                        {
                            methodsType.options.others.map(methodOther => (
                                <ContainerMethod key={`others-${methodOther.media_id}`}>
                                    <ContainerMethodData>
                                        <CardButton
                                            id={`others-${methodOther.media_id}`}
                                            title={'Selecione a forma de pagamento'} onClick={() => { onSetMethodPaymentSelectedOther(methodOther) }}>
                                            {methodPaymentSelected.others.length > 0 && methodPaymentSelected.others[0].media_id === methodOther.media_id ? <ImCheckboxChecked color="var(--primary)" size={28} /> : <ImCheckboxUnchecked color="#ccc" size={28} />}
                                        </CardButton>
                                        {
                                            methodOther.type?.toLowerCase() === 'pix' && (<PixIcon />)
                                        }
                                        <CardNumber><span>{methodOther.name}</span></CardNumber>
                                    </ContainerMethodData>
                                </ContainerMethod>
                            ))
                        }
                    </ContainerPayments>
                )
            }
            {
                methodsType.options.cashOnDelivery.length > 0 && (
                    <ContainerPayments>
                        <TitleCashOnDelivery>
                            {
                                addressSelected.type === "addressUser" ? (
                                    <>
                                        <span>Pague na entrega</span>
                                        <p><img src={InfoImage} alt="Informações" /> Frete será recalculado ao escolher pagamento na entrega</p>
                                    </>
                                ) : (
                                    <span>Pague na retirada</span>
                                )
                            }
                        </TitleCashOnDelivery>
                        {
                            methodsType.options.cashOnDelivery.map(methodDelivery => (
                                <ContainerMethod key={`delivery-${methodDelivery.media_id}`}>
                                    <ContainerMethodData>
                                        <CardButton 
                                        id={`delivery-${methodDelivery.media_id}`}
                                        title={'Selecione a forma de pagamento'} onClick={() => { onSetMethodPaymentSelectedCashOnDelivery(methodDelivery) }}>
                                            {methodPaymentSelected.cashOnDelivery.length > 0 && methodPaymentSelected.cashOnDelivery[0].media_id === methodDelivery.media_id ? <ImCheckboxChecked color="var(--primary)" size={28} /> : <ImCheckboxUnchecked color="#ccc" size={28} />}
                                        </CardButton>
                                        <CardName><span>{methodDelivery.name}</span></CardName>
                                    </ContainerMethodData>
                                </ContainerMethod>
                            ))
                        }
                    </ContainerPayments>
                )
            }
        </Container>
    )
}

export default PaymentList;